import React from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { ArrowRight, ArrowLeft, Robot, GitBranch, CurrencyDollar, HardDrives, Check } from '@phosphor-icons/react';

const serviceData = {
  'agentic-ai': {
    icon: <Robot size={48} weight="duotone" className="text-[#0052FF]" />,
    title: 'Agentic AI',
    subtitle: 'Autonomous AI Agents for Enterprise Automation',
    description: 'Deploy self-directed AI agents that learn, adapt, and execute complex enterprise workflows. Our Agentic AI platform integrates seamlessly into your existing operations to automate repetitive tasks, enhance decision-making, and drive operational excellence.',
    features: [
      'Autonomous multi-agent orchestration for complex workflows',
      'AI-Powered DevSecOps with proactive security scanning',
      'Intelligent threat detection and automated compliance',
      'Predictive incident management and auto-remediation',
      'Conversational AI platforms for enterprise support',
      'ML model training infrastructure and MLOps pipeline',
    ],
    useCases: ['Automated code review and security scanning', 'Intelligent ticket routing and resolution', 'Predictive infrastructure scaling', 'Automated compliance monitoring'],
  },
  'devops-automation': {
    icon: <GitBranch size={48} weight="duotone" className="text-[#0052FF]" />,
    title: 'DevOps Automation',
    subtitle: 'CI/CD, QA Testing & Application Monitoring',
    description: 'Comprehensive DevOps solutions as a service, streamlining software development, deployment, and automation to enhance agility, performance, cost-efficiency, and security. From pipeline orchestration to full-stack application performance monitoring.',
    features: [
      'CI/CD pipeline orchestration with zero-downtime deployments',
      'GitOps workflows with infrastructure-as-code',
      'Automated regression, performance, and security testing',
      'Blue-green and canary release strategies',
      'Full-stack APM tracking with distributed tracing',
      'Real-time alerting and log analytics',
    ],
    useCases: ['Continuous deployment pipelines', 'Automated QA testing suites', 'Application performance monitoring', 'Infrastructure automation'],
  },
  'cloud-finops': {
    icon: <CurrencyDollar size={48} weight="duotone" className="text-[#0052FF]" />,
    title: 'Cloud & FinOps',
    subtitle: 'Cost Optimization & Cloud Strategy',
    description: 'Maximize your cloud ROI with our specialized FinOps tooling and cloud strategy services. We provide granular cost analytics, AI-driven optimization recommendations, and seamless multi-cloud governance to ensure you only pay for what you use.',
    features: [
      'Real-time cloud cost analytics across AWS, Azure, GCP',
      'AI-driven savings recommendations and automation',
      'Reserved instance and spot instance management',
      'Next-gen EC2 instance type migration',
      'Multi-cloud governance and policy enforcement',
      'Budget alerting, forecasting, and waste elimination',
    ],
    useCases: ['Cloud cost reduction programs', 'Multi-cloud cost governance', 'Instance right-sizing and migration', 'FinOps team enablement'],
  },
  'infrastructure-compute': {
    icon: <HardDrives size={48} weight="duotone" className="text-[#0052FF]" />,
    title: 'Infrastructure & Compute',
    subtitle: 'Enterprise IaaS, Networking & Storage',
    description: 'Scalable infrastructure services from bare-metal servers to serverless functions. Deploy globally with our dual-region architecture, enterprise-grade networking, and managed storage solutions designed for mission-critical workloads.',
    features: [
      'Bare-metal and virtual machine provisioning',
      'Managed Kubernetes and container orchestration',
      'Serverless functions with zero infrastructure overhead',
      'VPC design, load balancing, and CDN services',
      'Block storage, object storage, and managed databases',
      '24/7 infrastructure monitoring and support',
    ],
    useCases: ['High-performance computing workloads', 'Container-native application hosting', 'Hybrid cloud infrastructure', 'Disaster recovery and failover'],
  },
};

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const service = serviceData[slug];

  if (!service) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-[72px] py-32 text-center">
          <h1 className="text-3xl font-bold text-[#0B132B]">Service not found</h1>
          <Link to="/services" className="text-[#0052FF] mt-4 inline-block">Back to Services</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid={`service-detail-${slug}`}>
      <Header />
      <div className="pt-[72px]">
        {/* Hero */}
        <div className="bg-[#0B132B] py-20 md:py-28">
          <div className="w-full px-6 md:px-10 lg:px-16 xl:px-24">
            <Link to="/services" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-6 transition-colors">
              <ArrowLeft size={16} /> Back to Services
            </Link>
            <div className="flex items-center gap-4 mb-4">
              {service.icon}
              <h1 className="text-4xl sm:text-5xl tracking-tighter font-black text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {service.title}
              </h1>
            </div>
            <p className="text-lg md:text-xl text-slate-300 max-w-3xl" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {service.subtitle}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="py-20 md:py-28">
          <div className="w-full px-6 md:px-10 lg:px-16 xl:px-24">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
              {/* Main content */}
              <div className="lg:col-span-3">
                <p className="text-base leading-relaxed text-[#475569] mb-10" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  {service.description}
                </p>
                <h3 className="text-xl font-semibold text-[#0B132B] mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Key Capabilities
                </h3>
                <ul className="space-y-4 mb-10">
                  {service.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <Check size={20} weight="bold" className="text-[#10B981] mt-0.5 flex-shrink-0" />
                      <span className="text-base text-[#475569]">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#0052FF] text-white text-base font-bold rounded-full hover:bg-[#0040CC] transition-colors duration-200"
                  data-testid="service-cta-btn"
                >
                  Get Started <ArrowRight size={18} weight="bold" />
                </Link>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-2">
                <div className="bg-[#F8FAFC] border border-slate-200 p-8">
                  <h4 className="text-lg font-semibold text-[#0B132B] mb-5" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    Use Cases
                  </h4>
                  <ul className="space-y-3">
                    {service.useCases.map((uc) => (
                      <li key={uc} className="flex items-start gap-3">
                        <ArrowRight size={14} weight="bold" className="text-[#0052FF] mt-1 flex-shrink-0" />
                        <span className="text-base text-[#475569]">{uc}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
